import { Building2, Calendar, Tag, UserCheck } from "lucide-react";
import { formatCurrency, formatNumber } from "@wtc/ui";
import type { Tickets } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact, HeaderMetric, HeaderSubtitleSegment } from "../model";
import { formatHeaderDate } from "../model";

/** Guarded against GetTickets by detail-header-model-coverage.test.ts. */
export const TICKET_HEADER_FIELDS = [
  "ticket_number",
  "category",
  "assigned_to",
  "company_id",
  "company_name",
  "due_date",
  "estimated_hours",
  "actual_hours",
  "billable_hours",
  "total_billed",
] as const satisfies readonly (keyof Tickets)[];

export type TicketHeaderData = Pick<Tickets, (typeof TICKET_HEADER_FIELDS)[number]>;

/** 1:1 scalar columns edited in the header (dropped from the inline panel). */
export const TICKET_HEADER_EDITABLE_FIELDS = [
  "due_date",
  "estimated_hours",
] as const satisfies readonly (keyof Tickets)[];
type TicketHeaderEditableField = (typeof TICKET_HEADER_EDITABLE_FIELDS)[number];

export function buildTicketHeaderModel(ticket: TicketHeaderData): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (ticket.company_name) {
    subtitleSegments.push({
      text: ticket.company_name,
      href: ticket.company_id ? `/companies/${ticket.company_id}` : undefined,
    });
  }
  if (ticket.category) subtitleSegments.push({ text: ticket.category });

  const facts: HeaderFact[] = [];
  if (ticket.ticket_number) {
    facts.push({ id: "ticket-number", icon: Tag, value: String(ticket.ticket_number) });
  }
  if (ticket.assigned_to) {
    // value is the app_users.id UUID; the header view resolves it to a name.
    facts.push({ id: "assigned", icon: UserCheck, label: "Assigned", value: ticket.assigned_to, kind: "user" });
  }
  const dueDate = formatHeaderDate(ticket.due_date);
  if (dueDate) {
    facts.push({
      id: "due-date",
      icon: Calendar,
      label: "Due",
      value: dueDate,
      fieldId: "due_date" satisfies TicketHeaderEditableField,
    });
  }
  if (!subtitleSegments.length && ticket.company_name) {
    facts.push({ id: "company", icon: Building2, value: ticket.company_name });
  }

  const metrics: HeaderMetric[] = [];
  const estimated = formatNumber(ticket.estimated_hours);
  if (estimated) {
    metrics.push({
      id: "estimated-hours",
      label: "Est. hours",
      value: `${estimated}h`,
      fieldId: "estimated_hours" satisfies TicketHeaderEditableField,
    });
  }

  const actual = formatNumber(ticket.actual_hours);
  if (actual) metrics.push({ id: "actual-hours", label: "Actual hours", value: `${actual}h` });

  const billable = formatNumber(ticket.billable_hours);
  if (billable) metrics.push({ id: "billable-hours", label: "Billable", value: `${billable}h` });

  const billed = formatCurrency(ticket.total_billed);
  if (billed) metrics.push({ id: "total-billed", label: "Billed", value: billed });

  return { subtitleSegments, facts, metrics };
}
